import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getBaseApiUrl, getEnvironment } from './EnvironmentManager';

interface ImageRequestParams {
  userId: string;
  action: string;
  prompt?: string;
  enhancedPrompt?: string; // Enhanced prompt for AI generation
  imageShape?: string;
  imageStyle?: string;
  imageModel?: string;
  imageTags?: string[];
  imageName?: string;
  vendorId?: string;
  base64Image?: string;
  sourceImageUrl?: string; // DEPRECATED: For generate-from-source action (backwards compat)
  sourceImageId?: string; // PREFERRED: Image ID to fetch from S3
  catalogItemId?: string;
  catalogItemVariantId?: string;
  // Enhanced remix metadata
  remixMode?: 'fine_tune' | 'retheme' | 'redesign';
  hardConstraints?: string;
  exploreAreas?: string;
  originalPrompt?: string;
}

interface ImageBaseUrls {
  [key: string]: string;
}
const imageBaseUrls: ImageBaseUrls = {
  localhost: 'https://dev-images.wallscapes.ai',
  dev: 'https://dev-images.wallscapes.ai',
  prod: 'https://images.wallscapes.ai',
};

const getImageApiUrl = () => {
  return `${getBaseApiUrl()}/image`;
};

const useImageApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  /**
   * Generate an image using the provided parameters.
   * 
   * @param params - Parameters for image generation.
   * @param params.userId - The ID of the user generating the image.
   * @param params.action - The action to perform (e.g., 'generate').
   * @param params.prompt - The prompt for image generation.
   * @param params.imageShape - The shape of the image (e.g., 'horizontal', 'vertical').
   * @param params.imageStyle - The style of the image.
   * @param params.imageModel - The model to use for image generation.
   * @param params.imageName - The name of the image.
   * @param params.imageTags - The tags for the image.
   * @param params.base64Image - The base64-encoded image data.
   * @param params.vendorId - The vendor ID for the image generation.
   * 
   * @returns {Promise<any>} - The generated image metadata.
   * @throws {Error} - If the API call fails or if the response is not successful. 
   */
  const generateImage = async (params: ImageRequestParams): Promise<any> => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(getImageApiUrl(), params, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (response?.status === 200 && response?.data?.status) {
        return response.data.data;
      } else {
        throw new Error('Image generation request failed or status is not success.');
      }
    } catch (error: Error | any) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        console.error('Rate limit hit:', error.response.data);
        // Extract the detailed message from backend if available
        const errorMessage = error.response?.data?.message || 'Image Generation Rate Limit Exceeded. Please try again later.';
        const resetDate = error.response?.data?.resetDate;
        const limitType = error.response?.data?.limitType;

        // Create enhanced error with metadata
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).resetDate = resetDate;
        (enhancedError as any).limitType = limitType;
        (enhancedError as any).statusCode = 429;

        throw enhancedError;
      } else {
        console.error('Error calling API:', JSON.stringify(error));
        throw new Error(`Generate Image Failed: ${error?.message}`);
      }
    }
  };

  /**
   * Upload an image using the provided parameters.
   * 
   * @param params - Parameters for image upload.
   * @param params.userId - The ID of the user uploading the image.
   * @param params.action - The action to perform (e.g., 'upload').
   * @param params.base64Image - The base64-encoded image data.
   * @param params.imageName - The name of the image.
   * @param params.imageTags - The tags for the image.
   * @param params.vendorId - The vendor ID for the image upload.
   * @param params.imageModel - The model to use for image upload.
   * @param params.imageShape - The shape of the image (e.g., 'horizontal', 'vertical').
   * @param params.imageStyle - The style of the image.
   * @param params.prompt - The prompt for image upload.
   * 
   * @returns 
   */
  const uploadImage = async (params: ImageRequestParams) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(getImageApiUrl(), params, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      if (response?.status === 200 && response?.data?.status) {
        return response.data.data;
      } else {
        throw new Error('No image URL found in the response or status is not success.');
      }
    } catch (error) {
      console.error('Error uploading user image:', error);
      throw new Error('Failed to upload user image.');
    }
  };

  const getImages = async (limit: number = 10, lastKey?: Record<string, any>): Promise<{ items: Array<Object>, lastKey: Record<string, any> | null }> => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      lastKey: lastKey
        ? JSON.stringify(lastKey) // Encode the lastKey as a JSON string
        : "",
    });

    const endpointUrl = `${getImageApiUrl()}?${params.toString()}`;
    try {
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success' && response?.data?.data) {
        return response.data.data;
      } else {
        throw new Error('No image data found in the response or status is not success.');
      }
    } catch (error) {
      console.error('Error calling API:', JSON.stringify(error));
      throw new Error('Failed to retrieve image data from the API');
    }
  };

  /**
   * Get a specific image by ID
   */
  const getImageById = async (imageId: string): Promise<any> => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(`${getImageApiUrl()}?imageId=${imageId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success') {
        return response.data.data;
      } else {
        throw new Error('Failed to get image by ID');
      }
    } catch (error: any) {
      console.error('Error getting image by ID:', {
        imageId,
        error: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        fullError: error
      });
      throw error;
    }
  };

  /**
   * Poll for image completion with 15s initial delay and 75s timeout
   */
  const pollForImageCompletion = async (imageId: string): Promise<any> => {
    const INITIAL_DELAY = 15000; // 15 seconds
    const POLL_INTERVAL = 3000;  // 3 seconds
    const MAX_TIMEOUT = 75000;   // 75 seconds total
    
    // Wait initial 15 seconds before first poll
    await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY));
    
    const startTime = Date.now();
    
    while (Date.now() - startTime < MAX_TIMEOUT) {
      try {
        const image = await getImageById(imageId);
        
        if (image.status === 'completed') {
          return image;
        } else if (image.status === 'failed') {
          throw new Error(image.errorMessage || 'Image generation failed');
        }
        
        // Still pending or processing, wait and try again
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
      } catch (error) {
        console.error('Error during polling:', error);
        throw error;
      }
    }
    
    throw new Error('Image generation timed out after 75 seconds');
  };

  /**
   * Generate image with async polling - returns immediately with imageId, then polls for completion
   */
  const generateImageAsync = async (params: ImageRequestParams): Promise<any> => {
    console.log('generateImageAsync called with params:', params);

    // First, trigger the generation and get the placeholder
    const pendingImage = await generateImage(params);
    console.log('Initial generateImage response:', pendingImage);

    if (pendingImage.status === 'pending') {
      console.log('Image is pending, starting polling for imageId:', pendingImage.imageId);
      // Poll for completion
      return await pollForImageCompletion(pendingImage.imageId);
    } else {
      console.log('Image not pending, returning immediately:', pendingImage);
      // If not pending, return immediately (shouldn't happen with new flow)
      return pendingImage;
    }
  };

  /**
   * Get the base image url for the environment
   */
  const getImageBaseUrl = () => {
    const environment = getEnvironment();
    return imageBaseUrls[environment];
  };

  return { 
    generateImage, 
    generateImageAsync, 
    uploadImage, 
    getImages, 
    getImageById,
    pollForImageCompletion,
    getImageBaseUrl 
  };
};

export default useImageApi;
